"use client";

import { useEffect } from "react";
import { useCanWritePreferenceCookies } from "@/lib/consent/region-context";
import { setClientCookie } from "@/lib/cookies/client";
import {
	HUNT_INTEREST_COOKIE,
	HUNT_INTEREST_MAX_AGE_SECONDS,
	jobRemoteToHuntPref,
	pushHuntInterestEntry,
	pushRecentJob,
	RECENT_JOBS_COOKIE,
	RECENT_JOBS_MAX_AGE_SECONDS,
} from "@/lib/personalization/anonymous-signals";

/** Reads a single cookie value by name from document.cookie (browser-only). */
function readCookie(name: string): string | null {
	const match = document.cookie
		.split("; ")
		.find((row) => row.startsWith(`${name}=`));
	return match ? match.slice(name.length + 1) : null;
}

/**
 * Invisible client component rendered on every job detail page. On mount it
 * appends this job onto two rolling cookies:
 *
 *   • `hunt_interest_v1` (v2 list) — skills/remote/industry of the last few jobs.
 *     The Hunt chatbot reads it to warm-start cold users, AND the guest feed
 *     scorer aggregates it so browsing carries into feed ranking, not just chat.
 *   • `ld_recent_jobs_v1` — id/title/company of the last few jobs, read
 *     client-side to render the "Recently viewed" rail on the feed + homepage.
 *
 * Both are rolling (newest-first, deduped by job id, capped) rather than
 * last-write-wins, so the browse *pattern* is preserved.
 */
export function JobInterestSignal({
	jobId,
	title,
	company,
	skills,
	remoteType,
	industry,
}: {
	jobId: string;
	title: string;
	company: string | null;
	skills: string[];
	remoteType: string | null;
	industry: string | null;
}) {
	const canWritePreferenceCookies = useCanWritePreferenceCookies();

	useEffect(() => {
		// Both cookies are personalization signals — gated on "preferences"
		// consent in prior-consent regions (EU/EEA/UK/CH).
		if (!canWritePreferenceCookies) return;

		// Rolling intent signal (skills/remote/industry) — server-read.
		if (skills.length > 0) {
			const next = pushHuntInterestEntry(readCookie(HUNT_INTEREST_COOKIE), {
				id: jobId,
				skills,
				remote: jobRemoteToHuntPref(remoteType),
				industry: industry ?? null,
				ts: Date.now(),
			});
			if (next) {
				setClientCookie(HUNT_INTEREST_COOKIE, next, {
					maxAgeSeconds: HUNT_INTEREST_MAX_AGE_SECONDS,
				});
			}
		}

		// Rolling render data for the "Recently viewed" rail — client-read.
		const recent = pushRecentJob(readCookie(RECENT_JOBS_COOKIE), {
			id: jobId,
			title,
			company,
		});
		if (recent) {
			setClientCookie(RECENT_JOBS_COOKIE, recent, {
				maxAgeSeconds: RECENT_JOBS_MAX_AGE_SECONDS,
			});
		}
	}, [
		jobId,
		title,
		company,
		skills,
		remoteType,
		industry,
		canWritePreferenceCookies,
	]);

	return null;
}
